import type { ResourceScope } from '@kop/contracts';
import type { VictimRegistryPage } from '@kop/incidents-emergency';
import type { RoutineTbmLedgerPage, HistoricalDailyTbmLedgerPage } from '@kop/tbm-firebase-adapter';
import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import { loadOperationsSummary } from './operations-summary-port';

export function seoulWorkDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
}

export function summarizeDashboardTbm(
  current: RoutineTbmLedgerPage,
  historical: HistoricalDailyTbmLedgerPage,
  day: string,
) {
  if (
    !current.exhausted ||
    current.nextCursor ||
    current.heldCount ||
    current.rows.length !== current.reconciliation.expectedCount ||
    !historical.exhausted ||
    historical.nextCursor ||
    historical.heldCount ||
    historical.rows.length !== historical.totalCount
  )
    return null;
  const rows = current.rows.filter(
    (row) =>
      row.date === day &&
      ['submitted', 'under-review', 'resubmitted', 'approved', 'closed'].includes(row.status),
  );
  const old = historical.rows.filter((row) => row.businessDate === day);
  const count = rows.length + old.length;
  const missing = old.some((row) => row.evidenceIssues.includes('MISSING_ATTENDANCE'));
  return {
    count,
    companies: new Set([...rows.map((row) => row.company), ...old.map((row) => row.contractorName)])
      .size,
    attendance: missing
      ? null
      : rows.reduce((sum, row) => sum + row.attendanceCount, 0) +
        old.reduce((sum, row) => sum + row.attendanceCount, 0),
    turnout: missing
      ? null
      : rows.reduce((sum, row) => sum + row.turnoutCount, 0) +
        old.reduce((sum, row) => sum + row.attendanceCount, 0),
  };
}

export function authoritativeVictimTotal(page: VictimRegistryPage): number {
  if (
    !page.summaryIncluded ||
    page.heldCount ||
    !Number.isSafeInteger(page.totalCount) ||
    page.totalCount < 0
  )
    throw Error('VICTIM_REGISTRY_SUMMARY_INCOMPLETE');
  return page.totalCount;
}

export async function readDashboard(input: {
  uid: string;
  scope: ResourceScope;
  day: string;
  month: string;
  features: readonly string[];
  fresh?: boolean;
}) {
  if (
    input.scope.tenantId !== 'kop' ||
    input.scope.kind === 'tenant' ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(input.scope.siteId)
  )
    throw Error('SCOPE_UNAVAILABLE');
  const scope = input.scope;
  const contractorId = scope.kind === 'contractor' ? scope.contractorId : null;
  const can = (id: string) => input.features.includes(id);
  const safe = async <T>(enabled: boolean, read: () => Promise<T>): Promise<T | null> =>
    enabled ? read().catch(() => null) : null;
  const [tbm, work, victimTotal, risk, operations] = await Promise.all([
    safe(can('tbm') && !!contractorId, async () => {
      const { getTbmAuthoritativeAdapter } = await import('../tbm/tbm-data-adapter');
      const adapter = getTbmAuthoritativeAdapter();
      const query = {
        tenantId: 'kop' as const,
        siteId: scope.siteId,
        actorId: input.uid,
        contractorId: contractorId ?? 'all-contractors',
        businessDate: input.day,
        pageSize: 30,
      };
      const [current, historical] = await Promise.all([
        adapter.loadRoutineTbmLedger(query),
        adapter.loadHistoricalDailyTbmLedger(query),
      ]);
      return summarizeDashboardTbm(current, historical, input.day);
    }),
    safe(can('work-permit'), async () => {
      const { getWorkLedgerPort } = await import('../work-control/work-ledger-port');
      const page = await getWorkLedgerPort().list(scope);
      if (!page.connected) return null;
      const rows = page.rows.filter(
        (row) =>
          row.lifecycle !== 'invalidated' &&
          seoulWorkDate(row.startsAt) <= input.day &&
          seoulWorkDate(row.endsAt) >= input.day,
      );
      return {
        rows,
        complete: page.exhausted === true && page.summary?.complete === true && !page.heldCount,
      };
    }),
    safe(can('incident'), async () => {
      const { createVictimRegistryPort } =
        await import('../incidents-emergency/victim-registry-data-adapter');
      const page = await createVictimRegistryPort(undefined, scope.siteId).list({
        contractorId,
        pageSize: 1,
        cursor: null,
        includeSummary: true,
        orderBy: 'occurredAt',
      });
      return authoritativeVictimTotal(page);
    }),
    safe(can('regular-risk') && !!contractorId, async () => {
      const { getRiskAuthoritativeAdapter } = await import('../risk-assessment/risk-data-adapter');
      const page = await getRiskAuthoritativeAdapter().listAssessments(scope, 'regular', 30);
      const start =
        input.month.slice(0, 4) + (Number(input.month.slice(5)) <= 6 ? '-01-01' : '-07-01');
      const end =
        input.month.slice(0, 4) + (Number(input.month.slice(5)) <= 6 ? '-06-30' : '-12-31');
      const rows = page.items.filter(
        (row) =>
          row.assessmentPeriod.startsOn &&
          row.assessmentPeriod.startsOn <= end &&
          row.assessmentPeriod.endsOn >= start,
      );
      return {
        total: rows.length,
        draft: rows.filter((row) => ['draft', 'revision-requested'].includes(row.stage)).length,
        complete: page.nextCursor === null,
      };
    }),
    safe(
      !contractorId &&
        [
          'tbm',
          'regular-risk',
          'safety-training',
          'evacuation-drill',
          'work-permit',
          'inspection-reporting',
        ].some(can),
      () => loadOperationsSummary({ ...input, siteId: scope.siteId, aggregation: 'daily' }),
    ),
  ]);
  return {
    tbm: contractorId ? tbm : (operations?.tbm ?? null),
    work,
    inspection: null,
    victimTotal,
    risk: contractorId ? risk : (operations?.risk ?? null),
    operations,
    at: Date.now(),
  };
}
export type DashboardRead = Awaited<ReturnType<typeof readDashboard>>;
const cache = createSessionKeyedReadCache<DashboardRead>(8);
export function loadDashboard(input: Parameters<typeof readDashboard>[0], force = false) {
  const key = JSON.stringify(input);
  const previous = cache.peek(key);
  return cache.read(
    key,
    () => readDashboard({ ...input, fresh: force }),
    force || !previous || Date.now() - previous.at > 60_000,
  );
}
